/**
 * Banner Ad Creator — Main Application Entry Point
 *
 * Initialises the Fabric.js canvas, loads default templates,
 * wires up UI event handlers, and coordinates all modules.
 */
import { CanvasManager } from './canvas/CanvasManager.js';
import { TemplateRenderer } from './canvas/TemplateRenderer.js';
import { Exporter } from './export/Exporter.js';
import { PdfExtractor } from './pdf/PdfExtractor.js';
import { PageSelector } from './pdf/PageSelector.js';
import { ImageCropper } from './background/ImageCropper.js';
import { PropertiesPanel } from './canvas/PropertiesPanel.js';
import { DEFAULT_TEMPLATES, getTemplateById, getTemplateList, getTemplatesBySize } from './templates/defaults.js';
import { waitForFonts } from './utils/fonts.js';
import { fileToDataUrl, loadImage } from './utils/imageUtils.js';
import { HistoryManager } from './utils/HistoryManager.js';

class BannerAdCreatorApp {
  constructor() {
    /** @type {CanvasManager} */
    this.canvasManager = null;
    /** @type {TemplateRenderer} */
    this.templateRenderer = null;
    /** @type {Exporter} */
    this.exporter = null;
    /** @type {PdfExtractor} */
    this.pdfExtractor = null;
    /** @type {PageSelector} */
    this.pageSelector = null;
    /** @type {ImageCropper} */
    this.imageCropper = null;
    /** @type {PropertiesPanel} */
    this.propertiesPanel = null;

    // Current state
    this._currentTemplateId = null;
    this._variables = {
      editionTitle: 'Bushwalk',
      editionSubtitle: 'Wild Waters',
      editionMonth: 'June',
      editionYear: 2026,
      editionBadgeText: 'June 2026 - Wild Waters',
      ctaText: 'DOWNLOAD',
      coverImage: null,
      backgroundImage: null,
      spreadPages: [],
    };

    this._bgImageSource = null; // Original background image element
    this._coverImageSource = null; // Original cover image element
    this._bgCropRegion = null;
    this._bgBlur = 15;
    this._bgBrightness = 30;

    this._renderDebounceTimer = null;
    this._history = new HistoryManager(50);
    this._historyDebounceTimer = null;
  }

  /**
   * Initialise the application.
   */
  async init() {
    console.log('Banner Ad Creator — Initialising...');

    // Wait for fonts
    await waitForFonts();

    // Initialise canvas
    this.canvasManager = new CanvasManager('main-canvas', {
      width: 1600,
      height: 300,
    });

    // Initialise template renderer
    this.templateRenderer = new TemplateRenderer(this.canvasManager);

    // Initialise exporter
    this.exporter = new Exporter(this.canvasManager);

    // Initialise PDF extractor
    this.pdfExtractor = new PdfExtractor();

    // Initialise page selector — uses separate callbacks for cover and spreads
    this.pageSelector = new PageSelector(
      document.getElementById('page-selector-strip'),
      {
        thumbnailHeight: 400,
        renderScale: 3.0,
        onCoverSelect: (coverCanvas) => this._onCoverSelected(coverCanvas),
        onPageSelect: (spreads) => this._onSpreadsSelected(spreads),
      }
    );

    // Initialise image cropper — it creates its own modal internally
    this.imageCropper = new ImageCropper(document.body);

    // Initialise properties panel
    this.propertiesPanel = new PropertiesPanel(
      document.getElementById('props-panel-body'),
      {
        onZoneUpdate: (zoneId, updates) => this._onZoneUpdate(zoneId, updates),
        onDeselect: () => this.canvasManager.canvas.discardActiveObject(),
      }
    );

    // Setup UI
    this._setupTemplateSelector();
    this._setupInputHandlers();
    this._setupFileUploads();
    this._setupZoomControls();
    this._setupExportButtons();
    this._setupCropButton();
    this._setupCanvasSelection();
    this._setupProjectSaveLoad();
    this._setupKeyboardShortcuts();

    // Load default template
    const defaultTemplate = DEFAULT_TEMPLATES[0];
    this._currentTemplateId = defaultTemplate.id;
    this._syncSlidersFromTemplate(defaultTemplate);
    this._applyTemplateVariables(defaultTemplate);
    await this._renderCurrentTemplate();

    console.log('Banner Ad Creator — Ready');
  }

  // =======================================================
  // Template Selection
  // =======================================================

  _setupTemplateSelector() {
    const select = document.getElementById('template-select');
    const sizeSelect = document.getElementById('size-select');

    // Populate template dropdown
    const templates = getTemplateList();
    templates.forEach(t => {
      const option = document.createElement('option');
      option.value = t.id;
      option.textContent = t.name;
      select.appendChild(option);
    });

    select.addEventListener('change', async (e) => {
      this._currentTemplateId = e.target.value;
      const template = getTemplateById(this._currentTemplateId);
      if (template) {
        // Update size selector to match
        sizeSelect.value = `${template.canvas.width}x${template.canvas.height}`;
        this._syncSlidersFromTemplate(template);
        this._applyTemplateVariables(template);
        await this._renderCurrentTemplate();
      }
    });

    sizeSelect.addEventListener('change', async (e) => {
      const [w, h] = e.target.value.split('x').map(Number);
      const matching = getTemplatesBySize(w, h);
      if (matching.length > 0) {
        const currentTemplate = getTemplateById(this._currentTemplateId);
        if (!currentTemplate || currentTemplate.canvas.width !== w || currentTemplate.canvas.height !== h) {
          this._currentTemplateId = matching[0].id;
          select.value = this._currentTemplateId;
          this._syncSlidersFromTemplate(matching[0]);
          this._applyTemplateVariables(matching[0]);
          await this._renderCurrentTemplate();
        }
      }
    });
  }

  // =======================================================
  // Input Handlers
  // =======================================================

  _setupInputHandlers() {
    const inputs = {
      'input-title': 'editionTitle',
      'input-subtitle': 'editionSubtitle',
      'input-badge-text': 'editionBadgeText',
      'input-cta-text': 'ctaText',
    };

    for (const [elementId, varKey] of Object.entries(inputs)) {
      const el = document.getElementById(elementId);
      if (el) {
        el.addEventListener('input', (e) => {
          this._variables[varKey] = e.target.value;
          this._debouncedRender();
        });
      }
    }

    // Month + Year → auto-update badge text
    const monthEl = document.getElementById('input-edition-month');
    const yearEl = document.getElementById('input-edition-year');

    const updateBadge = () => {
      const month = monthEl?.value || '';
      const year = yearEl?.value || '';
      const subtitle = this._variables.editionSubtitle || '';
      const badgeText = `${month} ${year} - ${subtitle}`;
      this._variables.editionBadgeText = badgeText;
      const badgeInput = document.getElementById('input-badge-text');
      if (badgeInput) badgeInput.value = badgeText;
      this._debouncedRender();
    };

    monthEl?.addEventListener('input', () => {
      this._variables.editionMonth = monthEl.value;
      updateBadge();
    });

    yearEl?.addEventListener('input', () => {
      this._variables.editionYear = parseInt(yearEl.value) || 2026;
      updateBadge();
    });

    // Also update badge when subtitle changes
    const subtitleEl = document.getElementById('input-subtitle');
    subtitleEl?.addEventListener('input', () => {
      this._variables.editionSubtitle = subtitleEl.value;
      updateBadge();
    });

    // Background blur/brightness sliders
    const blurSlider = document.getElementById('bg-blur');
    const brightnessSlider = document.getElementById('bg-brightness');

    blurSlider?.addEventListener('input', (e) => {
      this._bgBlur = parseInt(e.target.value);
      document.getElementById('bg-blur-value').textContent = this._bgBlur;
      const template = getTemplateById(this._currentTemplateId);
      if (template?.background?.type === 'image') {
        template.background.processing = template.background.processing || {};
        template.background.processing.blur = this._bgBlur;
      }
      this._debouncedRender();
    });

    brightnessSlider?.addEventListener('input', (e) => {
      this._bgBrightness = parseInt(e.target.value);
      document.getElementById('bg-brightness-value').textContent = this._bgBrightness;
      const template = getTemplateById(this._currentTemplateId);
      if (template?.background?.type === 'image') {
        template.background.processing = template.background.processing || {};
        template.background.processing.brightness = -(this._bgBrightness / 100);
      }
      this._debouncedRender();
    });

    // Magazine global settings — binding type + page count
    const magBinding = document.getElementById('input-mag-binding');
    const magPages = document.getElementById('input-mag-pages');

    const updateMagazine = () => {
      const template = getTemplateById(this._currentTemplateId);
      if (!template) return;
      this._ensureMagazineSettings(template);
      template.magazine.binding = magBinding?.value || 'saddle';
      template.magazine.pages = parseInt(magPages?.value) || 60;
      // Apply derived curl to all spread zones (resets per-zone overrides
      // when the global binding/pages baseline changes).
      const physical = this._deriveMagazinePhysical(template.magazine.binding, template.magazine.pages);
      for (const zone of template.zones) {
        if (zone.type === 'magazineSpread') {
          zone.transform = zone.transform || {};
          zone.transform.curveIntensity = physical.spreadCurve;
          zone.transform.binding = template.magazine.binding;
          zone.transform.pages = template.magazine.pages;
        }
      }
      this._debouncedRender(200);
    };

    magBinding?.addEventListener('change', updateMagazine);
    magPages?.addEventListener('input', updateMagazine);
  }

  // =======================================================
  // File Uploads
  // =======================================================

  _setupFileUploads() {
    // PDF upload
    const pdfInput = document.getElementById('input-pdf');
    pdfInput?.addEventListener('change', async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      await this._handlePdfUpload(file);
    });

    // Background image upload
    const bgInput = document.getElementById('input-bg-image');
    bgInput?.addEventListener('change', async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      await this._handleBackgroundUpload(file);
    });

    // Cover image upload (manual fallback)
    const coverInput = document.getElementById('input-cover-image');
    coverInput?.addEventListener('change', async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      await this._handleCoverUpload(file);
    });

    // Drag & drop enhancement for upload zones
    this._setupDragDrop('pdf-upload-zone', pdfInput);
    this._setupDragDrop('bg-upload-zone', bgInput);
    this._setupDragDrop('cover-upload-zone', coverInput);
  }

  _setupDragDrop(zoneId, inputEl) {
    const zone = document.getElementById(zoneId);
    if (!zone) return;

    zone.addEventListener('dragover', (e) => {
      e.preventDefault();
      zone.classList.add('dragover');
    });

    zone.addEventListener('dragleave', () => {
      zone.classList.remove('dragover');
    });

    zone.addEventListener('drop', (e) => {
      e.preventDefault();
      zone.classList.remove('dragover');
      if (e.dataTransfer.files.length > 0 && inputEl) {
        inputEl.files = e.dataTransfer.files;
        inputEl.dispatchEvent(new Event('change'));
      }
    });
  }

  async _handlePdfUpload(file) {
    const statusEl = document.getElementById('pdf-status');
    const progressEl = document.getElementById('pdf-progress');
    const progressFill = progressEl?.querySelector('.progress-fill');
    const selectorContainer = document.getElementById('page-selector-container');
    const uploadZone = document.getElementById('pdf-upload-zone');

    try {
      statusEl.style.display = 'block';
      statusEl.textContent = 'Loading PDF...';
      progressEl.style.display = 'block';
      progressFill.style.width = '0%';

      await this.pdfExtractor.loadPdf(file);
      const pageCount = this.pdfExtractor.getPageCount();
      statusEl.textContent = `Extracting ${pageCount} pages...`;

      // Show page selector
      selectorContainer.style.display = 'flex';
      await this.pageSelector.loadFromExtractor(this.pdfExtractor);

      statusEl.textContent = `${pageCount} pages extracted — select cover and spread pages below`;
      progressEl.style.display = 'none';
      uploadZone.classList.add('has-file');

      // Use cover (page 1) at high resolution for background use — already cached at 3.0 by PageSelector
      const coverPage = await this.pdfExtractor.extractPage(1, 3.0);
      if (coverPage) {
        this._coverImageSource = coverPage.canvas;
        this._variables.coverImage = coverPage.canvas;

        // Use cover as background if no separate background uploaded
        if (!this._bgImageSource) {
          this._bgImageSource = coverPage.canvas;
          this._variables.backgroundImage = coverPage.canvas;
          document.getElementById('bg-controls').style.display = 'block';
          document.getElementById('btn-crop-bg').style.display = 'inline-flex';
        }

        await this._renderCurrentTemplate();
      }

    } catch (err) {
      console.error('PDF upload failed:', err);
      statusEl.textContent = `Error: ${err.message}`;
      statusEl.style.color = 'var(--color-error)';
    }
  }

  async _handleBackgroundUpload(file) {
    try {
      const dataUrl = await fileToDataUrl(file);
      this._bgImageSource = await loadImage(dataUrl);
      this._variables.backgroundImage = this._bgImageSource;

      document.getElementById('bg-upload-zone').classList.add('has-file');
      document.getElementById('bg-controls').style.display = 'block';
      document.getElementById('btn-crop-bg').style.display = 'inline-flex';

      await this._renderCurrentTemplate();
    } catch (err) {
      console.error('Background upload failed:', err);
    }
  }

  async _handleCoverUpload(file) {
    try {
      const dataUrl = await fileToDataUrl(file);
      this._coverImageSource = await loadImage(dataUrl);
      this._variables.coverImage = this._coverImageSource;

      // Show preview
      const previewEl = document.getElementById('cover-preview');
      if (previewEl) {
        previewEl.innerHTML = `<img src="${dataUrl}" alt="Cover preview">`;
        previewEl.style.display = 'block';
      }

      document.getElementById('cover-upload-zone').classList.add('has-file');

      // Use as background if none set
      if (!this._bgImageSource) {
        this._bgImageSource = this._coverImageSource;
        this._variables.backgroundImage = this._bgImageSource;
        document.getElementById('bg-controls').style.display = 'block';
        document.getElementById('btn-crop-bg').style.display = 'inline-flex';
      }

      await this._renderCurrentTemplate();
    } catch (err) {
      console.error('Cover upload failed:', err);
    }
  }

  // =======================================================
  // Page Selection Callbacks
  // =======================================================

  /**
   * Called when cover page is selected/deselected in the PageSelector.
   * Both callbacks fire synchronously from PageSelector, so we use
   * _debouncedRender() so they coalesce into a single render pass.
   * @param {HTMLCanvasElement|null} coverCanvas
   */
  _onCoverSelected(coverCanvas) {
    if (coverCanvas) {
      this._coverImageSource = coverCanvas;
      this._variables.coverImage = coverCanvas;

      // Also update background if we don't have a separate one
      if (!this._bgImageSource || this._bgImageSource === this._variables.coverImage) {
        this._bgImageSource = coverCanvas;
        this._variables.backgroundImage = coverCanvas;
        document.getElementById('bg-controls').style.display = 'block';
        document.getElementById('btn-crop-bg').style.display = 'inline-flex';
      }
    } else {
      this._variables.coverImage = null;
    }

    this._debouncedRender();
  }

  /**
   * Called when spread pages are selected/deselected in the PageSelector.
   * @param {Array<{leftPage: HTMLCanvasElement, rightPage: HTMLCanvasElement}>} spreads
   */
  _onSpreadsSelected(spreads) {
    this._variables.spreadPages = spreads || [];
    this._debouncedRender();
  }

  // =======================================================
  // Crop Button (uses ImageCropper's built-in modal)
  // =======================================================

  _setupCropButton() {
    const cropBtn = document.getElementById('btn-crop-bg');

    cropBtn?.addEventListener('click', async () => {
      if (!this._bgImageSource) return;

      // Get current template aspect ratio
      const template = getTemplateById(this._currentTemplateId);
      const w = template?.canvas.width || 1600;
      const h = template?.canvas.height || 300;
      this.imageCropper.setAspectRatio(w, h);

      // Convert source to data URL for the cropper
      const canvas = document.createElement('canvas');
      const src = this._bgImageSource;
      canvas.width = src.width || src.naturalWidth;
      canvas.height = src.height || src.naturalHeight;
      canvas.getContext('2d').drawImage(src, 0, 0);

      // loadImage opens the modal and returns a promise
      const cropRegion = await this.imageCropper.loadImage(canvas.toDataURL());

      if (cropRegion) {
        this._bgCropRegion = cropRegion;
        this._variables._backgroundCropRegion = cropRegion;
        await this._renderCurrentTemplate();
      }
    });
  }

  // =======================================================
  // Zoom Controls
  // =======================================================

  _setupZoomControls() {
    document.getElementById('btn-zoom-in')?.addEventListener('click', () => {
      this.canvasManager.zoomIn();
    });

    document.getElementById('btn-zoom-out')?.addEventListener('click', () => {
      this.canvasManager.zoomOut();
    });

    document.getElementById('btn-zoom-fit')?.addEventListener('click', () => {
      this.canvasManager.fitToView();
    });
  }

  // =======================================================
  // Export Buttons
  // =======================================================

  _setupExportButtons() {
    // Main export button (exports current template size)
    document.getElementById('btn-export')?.addEventListener('click', async () => {
      await this.exporter.exportPNG();
    });

    // Export All — renders and exports every template at every size
    document.getElementById('btn-export-all')?.addEventListener('click', async () => {
      await this._exportAll();
    });

    // Specific size exports
    document.getElementById('btn-export-wide')?.addEventListener('click', async () => {
      await this._exportAtSize(1600, 300);
    });

    document.getElementById('btn-export-rect')?.addEventListener('click', async () => {
      await this._exportAtSize(1500, 750);
    });
  }

  /**
   * Export all templates — renders each one and downloads a PNG.
   */
  async _exportAll() {
    const prevTemplateId = this._currentTemplateId;
    const overlay = document.getElementById('render-overlay');
    const overlayText = document.getElementById('render-overlay-text');

    for (let i = 0; i < DEFAULT_TEMPLATES.length; i++) {
      const template = DEFAULT_TEMPLATES[i];
      if (overlay) overlay.style.display = 'flex';
      if (overlayText) overlayText.textContent = `Exporting ${template.name}… (${i + 1}/${DEFAULT_TEMPLATES.length})`;

      this._currentTemplateId = template.id;
      await this._renderCurrentTemplate();
      await this.exporter.exportPNG(`banner-${template.id}-${template.canvas.width}x${template.canvas.height}.png`);
    }

    if (overlay) overlay.style.display = 'none';
    this._currentTemplateId = prevTemplateId;
    await this._renderCurrentTemplate();
  }

  async _exportAtSize(width, height) {
    const template = getTemplateById(this._currentTemplateId);
    if (template && template.canvas.width === width && template.canvas.height === height) {
      // Already at the right size
      await this.exporter.exportPNG();
    } else {
      // Find a matching template and render at that size
      const matching = DEFAULT_TEMPLATES.filter(
        t => t.canvas.width === width && t.canvas.height === height
      );

      if (matching.length > 0) {
        // Temporarily render with matching template and export
        const prevTemplateId = this._currentTemplateId;
        this._currentTemplateId = matching[0].id;
        await this._renderCurrentTemplate();
        await this.exporter.exportPNG();

        // Restore previous template
        this._currentTemplateId = prevTemplateId;
        await this._renderCurrentTemplate();
      } else {
        await this.exporter.exportPNG();
      }
    }
  }

  // =======================================================
  // Keyboard Shortcuts
  // =======================================================

  _setupKeyboardShortcuts() {
    document.addEventListener('keydown', (e) => {
      // Ignore when typing in inputs
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      const ctrl = e.ctrlKey || e.metaKey;

      if (ctrl && e.key === 'e') {
        e.preventDefault();
        this.exporter.exportPNG();
      } else if (ctrl && e.key === 's') {
        e.preventDefault();
        this._saveProject();
      } else if (ctrl && e.key === 'o') {
        e.preventDefault();
        this._loadProject();
      } else if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        this.canvasManager.zoomIn();
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        this.canvasManager.zoomOut();
      } else if (e.key === '0') {
        e.preventDefault();
        this.canvasManager.fitToView();
      } else if (ctrl && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        this._undo();
      } else if ((ctrl && e.key === 'z' && e.shiftKey) || (ctrl && e.key === 'y')) {
        e.preventDefault();
        this._redo();
      } else if (e.key === 'Escape') {
        this.canvasManager.canvas.discardActiveObject();
        this.canvasManager.canvas.renderAll();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        const obj = this.canvasManager.canvas.getActiveObject();
        if (obj) {
          this.canvasManager.canvas.remove(obj);
          this.canvasManager.canvas.renderAll();
        }
      }
    });
  }

  /**
   * Capture a history snapshot. Called after each render with debounce.
   * @private
   */
  _pushHistory() {
    clearTimeout(this._historyDebounceTimer);
    this._historyDebounceTimer = setTimeout(() => {
      const template = getTemplateById(this._currentTemplateId);
      this._history.push({
        templateId: this._currentTemplateId,
        templateOverrides: template ? this._extractTemplateOverrides(template) : null,
        variables: { ...this._variables },
        bgBlur: this._bgBlur,
        bgBrightness: this._bgBrightness,
        bgCropRegion: this._bgCropRegion,
      });
    }, 500);
  }

  /**
   * Undo to the previous history state.
   * @private
   */
  _undo() {
    const state = this._history.undo();
    if (!state) return;
    this._restoreFromHistory(state);
  }

  /**
   * Redo to the next history state.
   * @private
   */
  _redo() {
    const state = this._history.redo();
    if (!state) return;
    this._restoreFromHistory(state);
  }

  /**
   * Restore app state from a history snapshot.
   * @param {object} state
   * @private
   */
  _restoreFromHistory(state) {
    if (state.variables) {
      this._variables = { ...this._variables, ...state.variables };
      this._syncInputsFromVariables();
    }
    if (state.bgBlur !== undefined) this._bgBlur = state.bgBlur;
    if (state.bgBrightness !== undefined) this._bgBrightness = state.bgBrightness;
    if (state.bgCropRegion !== undefined) {
      this._bgCropRegion = state.bgCropRegion;
      this._variables._backgroundCropRegion = state.bgCropRegion;
    }
    if (state.templateId) {
      this._currentTemplateId = state.templateId;
      const select = document.getElementById('template-select');
      if (select) select.value = state.templateId;
      if (state.templateOverrides) {
        const template = getTemplateById(state.templateId);
        if (template) this._applyTemplateOverrides(template, state.templateOverrides);
      }
    }
    this._renderCurrentTemplate();
  }

  // =======================================================
  // Project Save / Load
  // =======================================================

  _setupProjectSaveLoad() {
    document.getElementById('btn-save-project')?.addEventListener('click', () => {
      this._saveProject();
    });

    document.getElementById('btn-load-project')?.addEventListener('click', () => {
      this._loadProject();
    });
  }

  _saveProject() {
    const template = getTemplateById(this._currentTemplateId);
    const state = {
      version: 1,
      templateId: this._currentTemplateId,
      templateOverrides: template ? this._extractTemplateOverrides(template) : null,
      variables: {
        editionTitle: this._variables.editionTitle,
        editionSubtitle: this._variables.editionSubtitle,
        editionMonth: this._variables.editionMonth,
        editionYear: this._variables.editionYear,
        editionBadgeText: this._variables.editionBadgeText,
        ctaText: this._variables.ctaText,
      },
      bgBlur: this._bgBlur,
      bgBrightness: this._bgBrightness,
      bgCropRegion: this._bgCropRegion,
    };

    const blob = new Blob([JSON.stringify(state, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `banner-project-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  _loadProject() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.addEventListener('change', async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      try {
        const text = await file.text();
        const state = JSON.parse(text);

        // Restore variables
        if (state.variables) {
          Object.assign(this._variables, state.variables);
          this._syncInputsFromVariables();
        }

        // Restore background settings
        if (state.bgBlur !== undefined) this._bgBlur = state.bgBlur;
        if (state.bgBrightness !== undefined) this._bgBrightness = state.bgBrightness;
        if (state.bgCropRegion !== undefined) {
          this._bgCropRegion = state.bgCropRegion;
          this._variables._backgroundCropRegion = state.bgCropRegion;
        }

        // Restore template
        if (state.templateId) {
          this._currentTemplateId = state.templateId;
          const select = document.getElementById('template-select');
          if (select) select.value = state.templateId;

          // Apply template overrides (zone edits)
          if (state.templateOverrides) {
            const template = getTemplateById(state.templateId);
            if (template) this._applyTemplateOverrides(template, state.templateOverrides);
          }
        }

        await this._renderCurrentTemplate();
      } catch (err) {
        console.error('Failed to load project:', err);
        alert('Failed to load project file: ' + err.message);
      }
    });
    input.click();
  }

  /**
   * Extract serializable overrides from a template (zone bounds, styles, transforms).
   */
  _extractTemplateOverrides(template) {
    return {
      canvas: { ...template.canvas },
      zones: template.zones.map(z => ({
        id: z.id,
        bounds: { ...z.bounds },
        style: z.style ? { ...z.style } : undefined,
        transform: z.transform ? { ...z.transform } : undefined,
      })),
    };
  }

  /**
   * Apply saved overrides to a template in-place.
   */
  _applyTemplateOverrides(template, overrides) {
    if (overrides.canvas) {
      Object.assign(template.canvas, overrides.canvas);
    }
    if (overrides.zones) {
      for (const savedZone of overrides.zones) {
        const zone = template.zones.find(z => z.id === savedZone.id);
        if (zone) {
          if (savedZone.bounds) Object.assign(zone.bounds, savedZone.bounds);
          if (savedZone.style) Object.assign(zone.style = zone.style || {}, savedZone.style);
          if (savedZone.transform) Object.assign(zone.transform = zone.transform || {}, savedZone.transform);
        }
      }
    }
  }

  /**
   * Sync UI input fields from current variables.
   */
  _syncInputsFromVariables() {
    const map = {
      'input-title': this._variables.editionTitle,
      'input-subtitle': this._variables.editionSubtitle,
      'input-badge-text': this._variables.editionBadgeText,
      'input-cta-text': this._variables.ctaText,
      'input-edition-month': this._variables.editionMonth,
      'input-edition-year': this._variables.editionYear,
    };
    for (const [id, value] of Object.entries(map)) {
      const el = document.getElementById(id);
      if (el && value !== undefined) el.value = value;
    }
  }

  /**
   * Sync background blur/brightness sliders from a template's stored values.
   * Called when switching templates so sliders reflect the template, not
   * the previous template's slider state.
   * @param {object} template
   */
  _syncSlidersFromTemplate(template) {
    const processing = template?.background?.processing;
    if (processing) {
      if (processing.blur !== undefined) {
        this._bgBlur = processing.blur;
        const slider = document.getElementById('bg-blur');
        const valEl = document.getElementById('bg-blur-value');
        if (slider) slider.value = this._bgBlur;
        if (valEl) valEl.textContent = this._bgBlur;
      }
      if (processing.brightness !== undefined) {
        this._bgBrightness = Math.round(-processing.brightness * 100);
        const slider = document.getElementById('bg-brightness');
        const valEl = document.getElementById('bg-brightness-value');
        if (slider) slider.value = this._bgBrightness;
        if (valEl) valEl.textContent = this._bgBrightness;
      }
    }
  }

  /**
   * Apply a template's default variables (edition title, subtitle, etc.)
   * to the app state and sync UI inputs. Called on template switch and
   * initial load so each template starts with its own default text.
   * @param {object} template
   */
  _applyTemplateVariables(template) {
    if (!template.variables) return;
    const v = template.variables;
    if (v.editionTitle !== undefined) this._variables.editionTitle = v.editionTitle;
    if (v.editionSubtitle !== undefined) this._variables.editionSubtitle = v.editionSubtitle;
    if (v.editionMonth !== undefined) this._variables.editionMonth = v.editionMonth;
    if (v.editionYear !== undefined) this._variables.editionYear = v.editionYear;
    if (v.editionBadgeText !== undefined) this._variables.editionBadgeText = v.editionBadgeText;
    if (v.ctaText !== undefined) this._variables.ctaText = v.ctaText;
    this._syncInputsFromVariables();
  }

  // =======================================================
  // Canvas Selection → Properties Panel
  // =======================================================

  _setupCanvasSelection() {
    const canvas = this.canvasManager.canvas;

    canvas.on('selection:created', (e) => this._onSelectionChanged(e));
    canvas.on('selection:updated', (e) => this._onSelectionChanged(e));
    canvas.on('selection:cleared', () => this.propertiesPanel.clear());

    // Direct manipulation: shift+drag or double-click-drag a magazine to
    // orbit it in 3D; scroll to zoom. Plain click-drag moves it normally.
    canvas.on('mouse:down', (e) => this._onCanvasMouseDown(e));
    canvas.on('mouse:move', (e) => this._onCanvasMouseMove(e));
    canvas.on('mouse:up', () => this._onCanvasMouseUp());
    canvas.on('mouse:dblclick', (e) => this._onCanvasDblClick(e));
    canvas.on('mouse:wheel', (e) => this._onCanvasWheel(e));
  }

  /**
   * Whether a fabric object is an orbitable magazine zone.
   * @param {object} obj
   * @returns {boolean}
   */
  _isMagazineObject(obj) {
    if (!obj) return false;
    const t = obj.get('zoneType');
    return t === 'magazineCover' || t === 'magazineSpread';
  }

  /**
   * Look up the zone definition for a fabric object.
   * @param {string} zoneId
   * @returns {object|null}
   */
  _zoneById(zoneId) {
    const template = getTemplateById(this._currentTemplateId);
    return template?.zones.find((z) => z.id === zoneId) ?? null;
  }

  _onCanvasMouseDown(e) {
    // Shift+click on a magazine arms orbit mode immediately.
    if (e.e && e.e.shiftKey && this._isMagazineObject(e.target)) {
      this._orbitArmed = true;
    }
    // Only enter orbit mode if shift was held or a double-click just happened.
    if (!this._orbitArmed) return;
    const obj = e.target;
    if (!this._isMagazineObject(obj)) return;

    const zoneId = obj.get('zoneId');
    const zone = this._zoneById(zoneId);
    if (!zone) return;

    const t = zone.transform || (zone.transform = {});
    const p = e.scenePoint || e.pointer || { x: 0, y: 0 };
    const isCover = obj.get('zoneType') === 'magazineCover';
    this._orbit = {
      zoneId,
      startX: p.x,
      startY: p.y,
      startTilt: t.cameraTiltX ?? (isCover ? 35 : 45),
      startTurn: t.cameraRotateY ?? (isCover ? -20 : 0),
    };
    this._orbitArmed = false;
    this.templateRenderer.zoneFactory.draftMode = true;
    // Suppress Fabric's default move/scale while orbiting.
    obj.set({
      lockMovementX: true,
      lockMovementY: true,
      lockScalingX: true,
      lockScalingY: true,
      lockRotation: true,
    });
    this.canvasManager.canvas.defaultCursor = 'grabbing';
  }

  _onCanvasDblClick(e) {
    const obj = e.target;
    if (!this._isMagazineObject(obj)) return;
    // Arm orbit mode so the very next mouse:down starts orbiting.
    this._orbitArmed = true;
    // If the user holds the mouse down after the double-click, Fabric will
    // fire mouse:down again on the next drag — that's what we want.
  }

  _onCanvasMouseMove(e) {
    if (!this._orbit) return;
    const p = e.scenePoint || e.pointer;
    if (!p) return;
    const zone = this._zoneById(this._orbit.zoneId);
    if (!zone) return;

    const dx = p.x - this._orbit.startX;
    const dy = p.y - this._orbit.startY;
    const SENS = 0.15; // degrees per canvas unit
    const t = zone.transform || (zone.transform = {});
    t.cameraTiltX = Math.max(0, Math.min(90, this._orbit.startTilt + dy * SENS));
    t.cameraRotateY = Math.max(-90, Math.min(90, this._orbit.startTurn + dx * SENS));
    this._queueZoneRender(this._orbit.zoneId);
  }

  async _onCanvasMouseUp() {
    if (!this._orbit) return;
    const zoneId = this._orbit.zoneId;
    const obj = this.canvasManager.canvas.getActiveObject();
    this._orbit = null;
    this.canvasManager.canvas.defaultCursor = 'default';
    // Restore Fabric's normal move/scale/rotate now that orbit is done.
    if (obj) {
      obj.set({
        lockMovementX: false,
        lockMovementY: false,
        lockScalingX: false,
        lockScalingY: false,
        lockRotation: false,
      });
    }
    // Restore full quality and do a final crisp render.
    this.templateRenderer.zoneFactory.draftMode = false;
    await this.templateRenderer.rerenderZone(zoneId);
    const zone = this._zoneById(zoneId);
    if (zone) this.propertiesPanel.showZone(zone);
    this._pushHistory();
  }

  _onCanvasWheel(e) {
    const obj = this.canvasManager.canvas.getActiveObject();
    if (!this._isMagazineObject(obj)) return;
    e.e.preventDefault();
    e.e.stopPropagation();

    const zoneId = obj.get('zoneId');
    const zone = this._zoneById(zoneId);
    if (!zone) return;

    const t = zone.transform || (zone.transform = {});
    const cur = t.zoom ?? 1;
    const delta = e.e.deltaY < 0 ? 0.1 : -0.1;
    t.zoom = Math.max(1, Math.min(3, Math.round((cur + delta) * 100) / 100));

    this.templateRenderer.zoneFactory.draftMode = true;
    this._queueZoneRender(zoneId);

    // Settle to full quality shortly after the user stops scrolling.
    clearTimeout(this._wheelSettleTimer);
    this._wheelSettleTimer = setTimeout(async () => {
      this.templateRenderer.zoneFactory.draftMode = false;
      await this.templateRenderer.rerenderZone(zoneId);
      const z = this._zoneById(zoneId);
      if (z) this.propertiesPanel.showZone(z);
      this._pushHistory();
    }, 300);
  }

  /**
   * Coalesced single-zone re-render. Ensures only one 3D render runs at a time
   * and always renders the latest requested state (for smooth drag/zoom).
   * @param {string} zoneId
   */
  _queueZoneRender(zoneId) {
    this._pendingZoneId = zoneId;
    if (this._zoneRenderBusy) return;
    this._zoneRenderBusy = true;
    requestAnimationFrame(async () => {
      try {
        const id = this._pendingZoneId;
        this._pendingZoneId = null;
        if (id) await this.templateRenderer.rerenderZone(id);
      } finally {
        this._zoneRenderBusy = false;
        if (this._pendingZoneId) this._queueZoneRender(this._pendingZoneId);
      }
    });
  }

  _onSelectionChanged(e) {
    const obj = this.canvasManager.canvas.getActiveObject();
    if (!obj) {
      this.propertiesPanel.clear();
      return;
    }

    const zoneId = obj.get('zoneId');
    if (!zoneId) {
      this.propertiesPanel.clear();
      return;
    }

    const template = getTemplateById(this._currentTemplateId);
    if (!template) return;

    const zone = template.zones.find(z => z.id === zoneId);
    if (zone) {
      this.propertiesPanel.showZone(zone);
    }
  }

  /**
   * Handle zone property updates from the PropertiesPanel.
   * Applies live canvas updates where possible for instant visual feedback,
   * and debounces a full re-render only when needed (3D transforms).
   * @param {string} zoneId
   * @param {Record<string, *>} updates
   */
  _onZoneUpdate(zoneId, updates) {
    const template = getTemplateById(this._currentTemplateId);
    if (!template) return;

    const zone = template.zones.find(z => z.id === zoneId);
    if (!zone) return;

    // Apply dot-notation updates to the template zone definition
    for (const [key, value] of Object.entries(updates)) {
      if (key.includes('.')) {
        const parts = key.split('.');
        let target = zone;
        for (let i = 0; i < parts.length - 1; i++) {
          if (!target[parts[i]]) target[parts[i]] = {};
          target = target[parts[i]];
        }
        target[parts[parts.length - 1]] = value;
      }
    }

    // Find the Fabric object for this zone on the canvas
    const canvas = this.canvasManager.canvas;
    const fabricObj = canvas.getObjects().find(o => o.get('zoneId') === zoneId);

    if (fabricObj) {
      // Determine which properties changed
      const changedKeys = Object.keys(updates);
      const isBoundsChange = changedKeys.some(k => k.startsWith('bounds.'));
      const isStyleChange = changedKeys.some(k => k.startsWith('style.'));
      const isTransformChange = changedKeys.some(k => k.startsWith('transform.'));

      // Live-update position & size on the Fabric object
      if (isBoundsChange) {
        if (updates['bounds.x'] !== undefined) fabricObj.set('left', updates['bounds.x']);
        if (updates['bounds.y'] !== undefined) fabricObj.set('top', updates['bounds.y']);
        if (updates['bounds.width'] !== undefined) {
          const displayScale = fabricObj.get('zoneDisplayScale') || 1;
          if (fabricObj.get('zoneType') === 'magazineCover' || fabricObj.get('zoneType') === 'magazineSpread') {
            // For 3D zones, width change requires full re-render; just update bounds
            fabricObj.set('zoneBounds', { ...zone.bounds });
          } else {
            fabricObj.set('width', updates['bounds.width']);
          }
        }
        if (updates['bounds.height'] !== undefined) {
          if (fabricObj.get('zoneType') === 'magazineCover' || fabricObj.get('zoneType') === 'magazineSpread') {
            fabricObj.set('zoneBounds', { ...zone.bounds });
          } else {
            fabricObj.set('height', updates['bounds.height']);
          }
        }
        canvas.renderAll();
      }

      // Live-update text style properties
      if (isStyleChange && fabricObj.get('zoneType') === 'text') {
        const s = zone.style || {};
        if (s.fontFamily !== undefined) fabricObj.set('fontFamily', s.fontFamily);
        if (s.fontSize !== undefined) fabricObj.set('fontSize', s.fontSize);
        if (s.fontWeight !== undefined) fabricObj.set('fontWeight', s.fontWeight);
        if (s.fill !== undefined) fabricObj.set('fill', s.fill);
        if (s.textAlign !== undefined) fabricObj.set('textAlign', s.textAlign);
        canvas.renderAll();
      }

      // For badge/CTA groups, live-update child objects
      if (isStyleChange && (fabricObj.get('zoneType') === 'badge' || fabricObj.get('zoneType') === 'cta')) {
        const s = zone.style || {};
        const bgRect = fabricObj.getObjects?.()?.find(o => o.type === 'rect');
        const textObj = fabricObj.getObjects?.()?.find(o => o.type === 'text');
        if (bgRect && s.backgroundColor !== undefined) bgRect.set('fill', s.backgroundColor);
        if (bgRect && s.borderRadius !== undefined) { bgRect.set('rx', s.borderRadius); bgRect.set('ry', s.borderRadius); }
        if (textObj && s.textColor !== undefined) textObj.set('fill', s.textColor);
        if (textObj && s.fontSize !== undefined) textObj.set('fontSize', s.fontSize);
        if (textObj && s.fontWeight !== undefined) textObj.set('fontWeight', s.fontWeight);
        fabricObj.set('dirty', true);
        canvas.renderAll();
      }

      // 3D transform changes require a full re-render — debounce more aggressively
      if (isTransformChange) {
        this._debouncedRender(300);
        return;
      }

      // Bounds changes on 3D zones also require full re-render
      if (isBoundsChange && (fabricObj.get('zoneType') === 'magazineCover' || fabricObj.get('zoneType') === 'magazineSpread')) {
        this._debouncedRender(300);
        return;
      }
    }

    // Default: debounced full re-render
    this._debouncedRender();
  }

  // =======================================================
  // Rendering
  // =======================================================

  /**
   * Render the current template with current variables.
   */
  async _renderCurrentTemplate() {
    const template = getTemplateById(this._currentTemplateId);
    if (!template) {
      console.error('Template not found:', this._currentTemplateId);
      return;
    }

    // Inject background processing overrides from sliders
    const vars = { ...this._variables };
    if (this._bgCropRegion) {
      vars._backgroundCropRegion = this._bgCropRegion;
    }

    // Apply global magazine physical look (binding + pages) to all magazine
    // zones. Only set derived values if not already present in the transform —
    // saved values from the JSON or user edits must be preserved.
    // Cover thickness is derived here; spread curl (edgeLift) is set
    // in updateMagazine so per-zone Curl sliders aren't clobbered.
    const mag = this._ensureMagazineSettings(template);
    const physical = this._deriveMagazinePhysical(mag.binding, mag.pages);
    for (const zone of template.zones) {
      if (zone.type === 'magazineCover') {
        zone.transform = zone.transform || {};
        if (zone.transform.thickness === undefined) zone.transform.thickness = physical.coverThickness;
      } else if (zone.type === 'magazineSpread') {
        zone.transform = zone.transform || {};
        if (zone.transform.binding === undefined) zone.transform.binding = mag.binding;
        if (zone.transform.pages === undefined) zone.transform.pages = mag.pages;
      }
    }

    try {
      await this.templateRenderer.render(template, vars);
      this._pushHistory();
    } catch (err) {
      console.error('Render failed:', err);
    }
  }

  /**
   * Ensure a template has a global magazine settings block.
   * @param {object} template
   * @returns {{binding: string, pages: number}}
   */
  _ensureMagazineSettings(template) {
    if (!template.magazine) {
      template.magazine = { binding: 'saddle', pages: 60 };
    }
    return template.magazine;
  }

  /**
   * Derive the physical 3D look (cover spine thickness + spread gutter curl)
   * from the global binding type and page count.
   *
   * Perfect-bound magazines have a glued square spine whose depth grows roughly
   * linearly with page count, and their spreads dive more steeply into the
   * gutter. Saddle-stitched magazines are a folded staple — a thin spine that
   * is effectively capped regardless of page count, and their spreads lie much
   * flatter. Values are in scene units (cover width = 3 units).
   *
   * @param {'saddle'|'perfect'} binding
   * @param {number} pages
   * @returns {{coverThickness: number, spreadCurve: number}}
   */
  _deriveMagazinePhysical(binding, pages) {
    const p = Math.max(4, Math.min(400, Number(pages) || 60));
    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
    if (binding === 'perfect') {
      return {
        coverThickness: clamp(p * 0.002, 0.02, 0.5),
        spreadCurve: clamp(0.12 + p * 0.0008, 0.12, 0.35),
      };
    }
    // saddle stitch — thin folded spine, flatter spreads
    return {
      coverThickness: clamp(p * 0.0008, 0.01, 0.06),
      spreadCurve: clamp(0.05 + p * 0.0003, 0.05, 0.15),
    };
  }

  /**
   * Debounced render — coalesces rapid changes into a single render pass.
   * @param {number} [delay=100] Delay in ms
   */
  _debouncedRender(delay = 100) {
    clearTimeout(this._renderDebounceTimer);
    this._renderDebounceTimer = setTimeout(() => {
      this._renderCurrentTemplate();
    }, delay);
  }
}

// =======================================================
// Bootstrap
// =======================================================
const app = new BannerAdCreatorApp();

document.addEventListener('DOMContentLoaded', () => {
  app.init().catch(err => {
    console.error('Failed to initialise Banner Ad Creator:', err);
  });
});
